import { useState, useMemo } from 'react'
import { Share } from '@capacitor/share'
import { Capacitor } from '@capacitor/core'
import { posterUrl, profileUrl } from '../api/tmdb'
import { getDailyRating, saveDailyRating } from '../utils/storage'
import BrandLogo from './BrandLogo'
import {
  IconTrophy, IconStar, IconReel, IconRibbon,
  IconReplay, IconHome, IconChart, IconClapper,
  IconTicket, IconMessage, IconPlus, IconDot,
} from './icons'

const formatTime = (secs) => {
  const m = Math.floor(secs / 60).toString().padStart(2, '0')
  const s = (secs % 60).toString().padStart(2, '0')
  return `${m}:${s}`
}

function getRating(hops) {
  if (hops <= 1) return { Icon: IconTrophy, title: 'One Degree of Separation', tag: 'Legendary' }
  if (hops === 2) return { Icon: IconStar,   title: 'Two Hops to Glory',        tag: 'Brilliant' }
  if (hops === 3) return { Icon: IconReel,   title: 'Three-Reel Finish',        tag: 'Well Played' }
  return { Icon: IconRibbon, title: 'You Made the Connection', tag: 'Nice Work' }
}

function buildEmojiChain(chain) {
  return chain
    .map((item, i) => {
      if (item.type === 'movie') return '🎥'
      if (i === chain.length - 1) return '🏆'
      return item.gender === 1 ? '👩' : '👨'
    })
    .join(' → ')
}

const CONFETTI_COLORS = ['#C9A84C', '#debb5a', '#fff6c0', '#9a7a34', '#e8c96a', '#f0d878']

function Confetti() {
  const particles = useMemo(() =>
    Array.from({ length: 55 }, (_, i) => ({
      id: i,
      left: `${(Math.random() * 100).toFixed(1)}%`,
      delay: `${(Math.random() * 2.2).toFixed(2)}s`,
      duration: `${(1.6 + Math.random() * 1.4).toFixed(2)}s`,
      width: `${(5 + Math.random() * 7).toFixed(1)}px`,
      height: `${(8 + Math.random() * 10).toFixed(1)}px`,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      endRotate: `${Math.floor(Math.random() * 900 + 360)}deg`,
      isCircle: Math.random() > 0.55,
    }))
  , [])

  return (
    <div className="confetti" aria-hidden="true">
      {particles.map((p) => (
        <div
          key={p.id}
          className="confetti__piece"
          style={{
            left: p.left,
            animationDelay: p.delay,
            animationDuration: p.duration,
            width: p.width,
            height: p.isCircle ? p.width : p.height,
            background: p.color,
            borderRadius: p.isCircle ? '50%' : '2px',
            '--end-rotate': p.endRotate,
          }}
        />
      ))}
    </div>
  )
}

const RATING_OPTIONS = [
  { value: 'easy',   label: 'Easy',   emoji: '🟢', color: '#4caf50' },
  { value: 'medium', label: 'Medium', emoji: '🟡', color: '#e0b020' },
  { value: 'hard',   label: 'Hard',   emoji: '🔴', color: '#e0524d' },
]

export default function WinScreen({
  chain, startActor, endActor, moveCount, timeElapsed,
  onPlayAgain, onNewGame, onGoHome,
  isDailyChallenge, isHistoricalPuzzle = false, dailyNumber, dailyDate, cueCardsUsed = 0,
  opponentForfeited = false, earnedTicket, onViewStats,
  historicalPrevBest = null, isNewHistoricalBest = false,
}) {
  const [toastVisible, setToastVisible] = useState(false)
  const isRatable = (isDailyChallenge || isHistoricalPuzzle) && !!dailyDate
  const [rating, setRating] = useState(() =>
    isRatable ? getDailyRating(dailyDate) : null
  )

  const handleRate = (value) => {
    if (rating || !dailyDate) return
    setRating(value)
    saveDailyRating(dailyDate, value)
  }

  const showToast = () => {
    setToastVisible(true)
    setTimeout(() => setToastVisible(false), 2200)
  }

  const ratingEmoji = rating ? RATING_OPTIONS.find(o => o.value === rating)?.emoji : null

  const buildShareText = () => {
    const title = isDailyChallenge
      ? `Action! Daily #${dailyNumber} 🎬`
      : isHistoricalPuzzle
      ? `Action! Historical #${dailyNumber} 🎬`
      : 'Action! 🎬'
    const ratingLine = ratingEmoji ? ` • ${ratingEmoji}` : ''
    const statsLine = `${moveCount} hops • ${formatTime(timeElapsed)}${ratingLine}`
    const parts = [title, statsLine]
    if (cueCardsUsed > 0) parts.push(`🃏×${cueCardsUsed}`)
    parts.push(buildEmojiChain(chain))
    return parts.join('\n')
  }

  // Last-resort clipboard fallback for browsers without navigator.clipboard
  // (older Safari versions, insecure contexts). Used only when the modern API
  // throws on web/desktop — native iOS goes through Capacitor's Share plugin.
  const fallbackCopy = (text) => {
    try {
      const el = document.createElement('textarea')
      el.value = text
      el.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0'
      document.body.appendChild(el)
      el.focus(); el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
    } catch {}
  }

  // Wordle-style share: native OS share sheet on iOS/Android (Capacitor),
  // silent clipboard copy + toast on web/desktop. No custom modal.
  const handleShare = async () => {
    const text = buildShareText()
    if (Capacitor.isNativePlatform()) {
      try { await Share.share({ text }) } catch { /* user dismissed — ignore */ }
      return
    }
    try {
      await navigator.clipboard.writeText(text)
      showToast()
    } catch {
      fallbackCopy(text)
      showToast()
    }
  }

  const { Icon: RatingIcon, title: ratingTitle, tag: ratingTag } = getRating(moveCount)

  return (
    <div className="win-screen">
      <Confetti />

      <div className={`toast ${toastVisible ? 'toast--visible' : ''}`}>
        Copied to clipboard
      </div>

      <div className="win-screen__content">

        <div className="win-screen__title">
          <BrandLogo />
        </div>

        {isDailyChallenge && (
          <p className="win-screen__daily-label">Daily #{dailyNumber}</p>
        )}
        {isHistoricalPuzzle && (
          <p className="win-screen__daily-label">Historical — Daily #{dailyNumber}</p>
        )}

        <h2 className="win-screen__headline">You found the connection!</h2>

        <div className="win-screen__stats">
          <div className="win-screen__stat">
            <span className="win-screen__stat-value">{moveCount}</span>
            <span className="win-screen__stat-label">hops</span>
          </div>
          <div className="win-screen__stat-divider">·</div>
          <div className="win-screen__stat">
            <span className="win-screen__stat-value">{formatTime(timeElapsed)}</span>
            <span className="win-screen__stat-label">time</span>
          </div>
          {cueCardsUsed > 0 && (
            <>
              <div className="win-screen__stat-divider">·</div>
              <div className="win-screen__stat">
                <span className="win-screen__stat-value">
                  <IconClapper size={28} aria-hidden="true" /> ×{cueCardsUsed}
                </span>
                <span className="win-screen__stat-label">cue cards</span>
              </div>
            </>
          )}
        </div>

        {opponentForfeited && (
          <div className="win-screen__forfeit-badge">Opponent forfeited</div>
        )}

        <div className="win-screen__rating">
          <RatingIcon size={18} aria-hidden="true" />
          <span>{ratingTitle} — {ratingTag}!</span>
        </div>

        {isHistoricalPuzzle && historicalPrevBest && (
          <div className={`win-screen__best-banner${isNewHistoricalBest ? ' win-screen__best-banner--new' : ''}`}>
            {isNewHistoricalBest ? (
              <><IconTrophy size={14} aria-hidden="true" /> New best!</>
            ) : (
              `Your best is still ${historicalPrevBest.moves} hops`
            )}
          </div>
        )}

        {earnedTicket && (
          <div className="win-screen__ticket-earned">
            <IconTicket size={14} aria-hidden="true" /> +1 Ticket Earned
          </div>
        )}

        <p className="win-screen__route">
          {startActor?.name} <span>→</span> {endActor?.name}
        </p>

        <div className="win-screen__chain">
          {chain.map((item, i) => (
            <div key={i} className="win-screen__chain-item">
              {i > 0 && <span className="win-screen__arrow">→</span>}
              {item.type === 'actor' ? (
                <div className="win-screen__node win-screen__node--actor">
                  {item.profile_path && (
                    <img src={profileUrl(item.profile_path, 'w185')} alt={item.name} />
                  )}
                  <span>{item.name}</span>
                </div>
              ) : (
                <div className="win-screen__node win-screen__node--movie">
                  {item.poster_path && (
                    <img src={posterUrl(item.poster_path, 'w92')} alt={item.title} />
                  )}
                  <span>{item.title}</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {isRatable && (
          <div className="difficulty-rating">
            <p className="difficulty-rating__label">How tough was today's shoot?</p>
            <div className="difficulty-rating__pills">
              {RATING_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  className={[
                    'difficulty-rating__pill',
                    rating === opt.value ? 'difficulty-rating__pill--selected' : '',
                    rating && rating !== opt.value ? 'difficulty-rating__pill--dimmed' : '',
                  ].join(' ').trim()}
                  onClick={() => handleRate(opt.value)}
                  disabled={!!rating}
                >
                  <IconDot size={11} color={opt.color} style={{ verticalAlign: 'middle' }} /> {opt.label}
                </button>
              ))}
            </div>
            {rating && (
              <p className="difficulty-rating__community">
                Most players found this <IconDot size={11} color={RATING_OPTIONS.find(o => o.value === rating)?.color} style={{ verticalAlign: 'middle' }} /> {rating.charAt(0).toUpperCase() + rating.slice(1)}
              </p>
            )}
          </div>
        )}

        {/* Action row: 3 labeled circles. Layout depends on context:
            • Daily win  → Stats (left)      · Home (center) · Share    (right)
            • Normal/H2H → Play Again (left) · Home (center) · New Game (right)
            Share = native OS share sheet (Capacitor) or clipboard fallback.
            New Game = back to actor picker via `onNewGame`. */}
        <div className="win-screen__actions">
          {isDailyChallenge && onViewStats ? (
            <div className="win-action">
              <button
                className="win-circle-btn win-circle-btn--inverse"
                aria-label="See stats"
                title="See Stats"
                onClick={onViewStats}
              >
                <IconChart size={24} />
              </button>
              <span className="win-action__label">Stats</span>
            </div>
          ) : (
            <div className="win-action">
              <button
                className="win-circle-btn win-circle-btn--inverse"
                aria-label="Play again"
                title="Play Again"
                onClick={onPlayAgain}
              >
                <IconReplay size={24} />
              </button>
              <span className="win-action__label">Play Again</span>
            </div>
          )}

          <div className="win-action">
            <button
              className="win-circle-btn win-circle-btn--primary"
              aria-label="Home"
              title="Home"
              onClick={onGoHome}
            >
              <IconHome size={24} />
            </button>
            <span className="win-action__label">Home</span>
          </div>

          {isDailyChallenge && onViewStats ? (
            <div className="win-action">
              <button
                className="win-circle-btn win-circle-btn--outline"
                aria-label="Share result"
                title="Share"
                onClick={handleShare}
              >
                <IconMessage size={24} />
              </button>
              <span className="win-action__label">Share</span>
            </div>
          ) : (
            <div className="win-action">
              <button
                className="win-circle-btn win-circle-btn--outline"
                aria-label="Start new game"
                title="New Game"
                onClick={onNewGame}
              >
                <IconPlus size={24} />
              </button>
              <span className="win-action__label">New Game</span>
            </div>
          )}
        </div>

      </div>
    </div>
  )
}
